// src/pages/Onboarding.tsx
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { authFetch } from "../lib/auth";

type OnboardingStatus = {
  ekyc_status: string | null;
  account_status: string | null;
};

export default function Onboarding() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState<OnboardingStatus>({
    ekyc_status: user?.ekyc_status ?? null,
    account_status: user?.account_status ?? null,
  });

  const [loading, setLoading] = useState(true);

  // 최신 상태를 /api/me, /api/ekyc/status, /api/onewon/status 로 다시 한 번 가져와서 반영
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        let ekyc_status = user?.ekyc_status ?? null;
        let account_status = user?.account_status ?? null;

        // 1) /api/me 에서 기본값
        try {
          const res = await authFetch("/api/me");
          if (res.ok) {
            const data: any = await res.json().catch(() => ({}));
            if (data?.ok && data.user) {
              if (data.user.ekyc_status) ekyc_status = data.user.ekyc_status;
              if (data.user.account_status)
                account_status = data.user.account_status;
            }
          }
        } catch (e) {
          console.warn("[Onboarding] /api/me error", e);
        }

        // 2) 신분증 인증 상태 (별도 테이블 기준)
        try {
          const res = await authFetch("/api/ekyc/status");
          if (res.ok) {
            const j: any = await res.json().catch(() => ({}));
            if (j?.status === "verified") {
              ekyc_status = "verified";
            }
          }
        } catch (e) {
          console.warn("[Onboarding] /api/ekyc/status error", e);
        }

        // 3) 1원 인증 상태
        try {
          const res = await authFetch("/api/onewon/status");
          if (res.ok) {
            const j: any = await res.json().catch(() => ({}));
            if (j?.status === "verified") {
              account_status = "verified";
            }
          }
        } catch (e) {
          console.warn("[Onboarding] /api/onewon/status error", e);
        }

        if (!cancelled) {
          setStatus({ ekyc_status, account_status });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // ✅ 인증이 이미 조건을 만족하면 next 로 자동 이동
  useEffect(() => {
    if (loading) return;

    const ekycDone = status.ekyc_status === "verified";
    const accountDone = status.account_status === "verified";

    // 현재 유저가 소셜 계정인지 여부
    const isSocial =
      !!(user as any)?.is_social ||
      !!(user as any)?.social_provider ||
      !!(user as any)?.social_id;

    // ?next=/pay 같은 파라미터
    const search = new URLSearchParams(location.search);
    const nextParam = search.get("next") || "/start";

    // 소셜 가입자는 "신분증 or 1원" 둘 중 하나만 완료하면 통과
    // 일반 가입자는 둘 다 완료해야 통과
    const ready = isSocial ? ekycDone || accountDone : ekycDone && accountDone;

    if (ready) {
      nav(nextParam, { replace: true });
    }
  }, [loading, status.ekyc_status, status.account_status, user, location.search, nav]);

  const ekycDone = status.ekyc_status === "verified";
  const accountDone = status.account_status === "verified";

  const ekycLabel = ekycDone ? "인증완료" : "인증필요";
  const accountLabel = accountDone ? "인증완료" : "인증필요";

  const ekycBadgeClass = ekycDone
    ? "bg-emerald-100 text-emerald-700"
    : "bg-amber-100 text-amber-700";
  const accountBadgeClass = accountDone
    ? "bg-emerald-100 text-emerald-700"
    : "bg-amber-100 text-amber-700";

  const search = new URLSearchParams(location.search);
  const nextParam = search.get("next") || "/start";

  const handleGoEkyc = () => {
    const url = `/ekyc/reverify?next=${encodeURIComponent("/onboarding")}`;
    nav(url);
  };

  const handleGoAccount = () => {
    const url = `/account/verify?next=${encodeURIComponent("/onboarding")}`;
    nav(url);
  };

  // 현재 유저가 소셜 계정인지 여부
  const isSocial =
    !!(user as any)?.is_social ||
    !!(user as any)?.social_provider ||
    !!(user as any)?.social_id;

  // 설명 문구 & 어떤 카드를 보여줄지 결정
  let descriptionText = "";
  let showEkycCard = true;
  let showAccountCard = true;

  if (isSocial) {
    // ✅ 소셜 가입자: 둘 중 하나만 완료해도 됨 → 두 카드 모두 노출
    descriptionText =
      "카카오 또는 네이버 가입자는 서비스 이용을 위해 신분증 인증 또는 1원 계좌 인증을 완료해주세요.";
    showEkycCard = true;
    showAccountCard = true;
  } else {
    // ✅ 일반 가입자
    if (ekycDone && !accountDone) {
      descriptionText =
        "서비스 이용을 위해 1원 계좌 인증을 완료해주세요.";
      showEkycCard = false;
      showAccountCard = true;
    } else if (!ekycDone && accountDone) {
      descriptionText =
        "서비스 이용을 위해 신분증 인증을 완료해주세요.";
      showEkycCard = true;
      showAccountCard = false;
    } else if (!ekycDone && !accountDone) {
      descriptionText =
        "서비스 이용을 위해 신분증 인증과 1원 계좌 인증을 모두 완료해주세요.";
      showEkycCard = true;
      showAccountCard = true;
    } else {
      // 둘 다 이미 완료된 상태에서 들어온 예외 상황
      descriptionText =
        "본인 인증이 완료되었습니다. 계속해서 서비스를 이용하실 수 있습니다.";
      showEkycCard = false;
      showAccountCard = false;
    }
  }

  // 로딩 중에는 살짝만 비워두기 (배경색만 유지)
  if (loading) {
    return <div className="min-h-screen bg-slate-50" />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">
          본인 인증을 완료해 주세요
        </h1>
        <p className="mt-2 text-sm text-slate-600">{descriptionText}</p>

        <div className="mt-8 space-y-4">
          {/* 1. 신분증 인증 (필요한 경우에만 노출) */}
          {showEkycCard && (
            <section className="flex items-center justify-between rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70">
              <div className="flex flex-1 items-center gap-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-50 text-sm font-semibold text-brand-700">
                  1
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold text-slate-900">
                      신분증 인증
                    </h2>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${ekycBadgeClass}`}
                    >
                      현재 상태: {ekycLabel}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    주민등록증/운전면허증을 촬영하여 본인 여부를 확인합니다.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleGoEkyc}
                className="ml-4 rounded-xl border border-brand-500 px-4 py-2 text-xs font-semibold text-brand-600 hover:bg-brand-50"
              >
                신분증 인증하기
              </button>
            </section>
          )}

          {/* 2. 1원 계좌 인증 (필요한 경우에만 노출) */}
          {showAccountCard && (
            <section className="flex items-center justify-between rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70">
              <div className="flex flex-1 items-center gap-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-50 text-sm font-semibold text-indigo-700">
                  2
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold text-slate-900">
                      1원 계좌 인증
                    </h2>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${accountBadgeClass}`}
                    >
                      현재 상태: {accountLabel}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    주류페이에서 계좌로 1원을 송금하고,
                    송금자명을 입력해 계좌를 확인합니다.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleGoAccount}
                className="ml-4 rounded-xl border border-indigo-500 px-4 py-2 text-xs font-semibold text-indigo-600 hover:bg-indigo-50"
              >
                1원 인증하기
              </button>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
